import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import SalifyFallback from "../salify-fallback/default";
import { FaFacebook, FaLinkedin, FaXTwitter } from "react-icons/fa6";
import { FiFilter, FiChevronDown, FiChevronUp, FiX } from "react-icons/fi";
import clientFetcher from "src/lib/fetcher/client";
import { resolveCmsDomain } from "src/lib/utils/cms-domain";

export interface BlogPost {
  id?: number;
  name: string;
  description?: string;
  thumbnail?: string;
  slug: string;
  date?: string;
  author?: string;
  category?: string;
  tags?: string | string[]; // Can be comma-separated string or array
  lang?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

interface BlogWrapperProps {
  className?: string;
  title?: string;
  columns?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
  gap?: number;
}

// Number of items to display per scroll batch
const ITEMS_PER_BATCH = 15;

// Modern soft color palette
const COLORS = [
  "#FEE4E2", // Soft pink
  "#FFECD1", // Soft peach
  "#FFF8C2", // Soft yellow
  "#D4F8E8", // Soft mint
  "#CDE6FF", // Soft blue
  "#E0D7FF", // Soft lavender
  "#FFD6F5", // Soft rose
  "#D7F9FF", // Soft cyan
];

// Fixed height that fits image (128px) + padding (32px) + title (48px) + description (48px) + share buttons (40px) = ~296px
const FIXED_ITEM_HEIGHT = 260;

// Device type based on screen width
type DeviceType = "mobile" | "fold" | "tablet" | "desktop";

const getDeviceType = (width: number): DeviceType => {
  if (width < 480) return "mobile";
  if (width < 768) return "fold"; // Fold devices unfolded, small tablets
  if (width < 1024) return "tablet";
  return "desktop";
};

// Get responsive column count
const getColumnCount = (width: number, columns?: BlogWrapperProps["columns"]): number => {
  const device = getDeviceType(width);

  // Mobile: always 1 column
  if (device === "mobile") return 1;

  // Fold devices (480-768px): 2 columns
  if (device === "fold") return 2;

  if (!columns) {
    // Tablet (768-1024px): 2 columns
    if (device === "tablet") return 2;
    // Desktop: 3 columns
    return 3;
  }

  if (width >= 1440 && columns.lg) return columns.lg;
  if (width >= 1024 && columns.lg) return columns.lg;
  if (width >= 768 && columns.md) return columns.md;
  if (width >= 640 && columns.sm) return columns.sm;
  return columns.base || 3;
};

interface ItemStyle {
  height: number;
  backgroundColor: string;
}

interface ItemData {
  post: BlogPost;
  key: string | number;
  style: ItemStyle;
  measuredHeight: number | null;
  column: number | null;
}

// Get unique identifier for a post
// Always include index to ensure uniqueness even if posts have duplicate slugs/ids
const getPostKey = (post: BlogPost, index: number): string | number => {
  if (post.id != null) return `${post.id}-${index}`;
  if (post.slug) return `${post.slug}-${index}`;
  return `post-${index}`;
};

export const BlogWrapper: React.FC<BlogWrapperProps> = ({
  className = "",
  title,
  columns,
  gap: propGap,
}) => {
  const sectionTitle = (title || "Bài viết").trim() || "Bài viết";
  // Responsive gap based on screen size
  const [windowWidth, setWindowWidth] = useState(1024);
  const [nativeShareAvailable, setNativeShareAvailable] = useState(false);

  const responsiveGap = useMemo(() => {
    if (propGap !== undefined) return propGap;
    if (windowWidth < 480) return 12; // mobile
    if (windowWidth < 768) return 14; // fold
    if (windowWidth < 1024) return 16; // tablet
    return 20; // desktop
  }, [windowWidth, propGap]);

  const gap = responsiveGap;
  const blogsRaw: BlogPost[] = SalifyFallback.useSelect("output:articles.data");

  // All posts from SSR data + API pagination
  const [allLoadedPosts, setAllLoadedPosts] = useState<BlogPost[]>([]);
  const [displayedPosts, setDisplayedPosts] = useState<BlogPost[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);

  // Initialize with SSR data
  useEffect(() => {
    if (blogsRaw && blogsRaw.length > 0 && allLoadedPosts.length === 0) {
      setAllLoadedPosts(blogsRaw);
      // If we got less than a full page, there's no more data
      setHasMoreData(blogsRaw.length >= ITEMS_PER_BATCH);
    }
  }, [blogsRaw, allLoadedPosts.length]);

  const observerTarget = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [itemData, setItemData] = useState<Map<string | number, ItemData>>(new Map());
  const [measuredHeights, setMeasuredHeights] = useState<Map<string | number, number>>(new Map());
  const [columnsLayout, setColumnsLayout] = useState<Array<Array<string | number>>>([]);
  const [selectedFacets, setSelectedFacets] = useState<Set<string>>(new Set());
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [copiedPostId, setCopiedPostId] = useState<string | number | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Calculate device type for responsive behavior
  const deviceType = useMemo(() => getDeviceType(windowWidth), [windowWidth]);

  // Extract and parse tags from posts (handles both array and comma-separated string)
  const extractTags = useCallback((post: BlogPost): string[] => {
    if (!post.tags) return [];
    // If tags is already an array, return it directly
    if (Array.isArray(post.tags)) {
      return post.tags.filter((tag) => tag && tag.length > 0);
    }
    // Otherwise, split comma-separated string
    return post.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }, []);

  // Get all unique tags with counts from all loaded posts
  const facetData = useMemo(() => {
    const tagCounts = new Map<string, number>();
    allLoadedPosts.forEach((post) => {
      const tags = extractTags(post);
      tags.forEach((tag) => {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      });
    });
    return Array.from(tagCounts.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count); // Sort by count descending
  }, [allLoadedPosts, extractTags]);

  // Filter posts based on selected facets
  useEffect(() => {
    if (selectedFacets.size === 0) {
      setFilteredPosts(allLoadedPosts);
    } else {
      const filtered = allLoadedPosts.filter((post) => {
        const postTags = extractTags(post);
        // Post must have at least one of the selected facets
        return postTags.some((tag) => selectedFacets.has(tag));
      });
      setFilteredPosts(filtered);
    }
  }, [allLoadedPosts, selectedFacets, extractTags]);

  // Update displayed posts when filtered posts change
  useEffect(() => {
    setDisplayedPosts(filteredPosts);
  }, [filteredPosts]);

  // Initialize item data with consistent heights and colors
  useEffect(() => {
    const newItemData = new Map<string | number, ItemData>();
    const usedColors = new Set<string>();
    let colorCounter = 0;

    displayedPosts.forEach((post, index) => {
      // Use the actual index in displayedPosts array to ensure unique keys
      const key = getPostKey(post, index);

      // Assign unique colors to each post
      // Cycle through colors, ensuring each post gets a different color
      let backgroundColor = COLORS[colorCounter % COLORS.length];

      // If we have more posts than colors, ensure we don't repeat until all colors are used
      // This ensures each post on the page gets a different color
      if (usedColors.has(backgroundColor) && colorCounter < COLORS.length) {
        // Find next unused color
        for (let i = 0; i < COLORS.length; i++) {
          const testColor = COLORS[(colorCounter + i) % COLORS.length];
          if (!usedColors.has(testColor)) {
            backgroundColor = testColor;
            break;
          }
        }
      }

      usedColors.add(backgroundColor);
      colorCounter++;

      newItemData.set(key, {
        post,
        key,
        style: {
          height: FIXED_ITEM_HEIGHT,
          backgroundColor,
        },
        measuredHeight: null,
        column: null,
      });
    });
    setItemData(newItemData);
  }, [displayedPosts]);

  // Handle window resize
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateWidth = () => {
      setWindowWidth(window.innerWidth);
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setNativeShareAvailable(
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && "share" in navigator,
    );
  }, []);

  // Calculate column count based on window width
  const columnCount = useMemo(() => {
    return getColumnCount(windowWidth, columns);
  }, [windowWidth, columns]);

  // Distribute items to shortest columns AFTER heights are measured
  const distributeToColumns = useCallback(() => {
    if (columnCount === 0 || displayedPosts.length === 0) {
      setColumnsLayout([]);
      return;
    }

    const columnHeights = new Array(columnCount).fill(0);
    const columns: Array<Array<string | number>> = Array.from({ length: columnCount }, () => []);

    // Create array of items with their measured heights
    const itemsWithHeights = displayedPosts
      .map((post, index) => {
        const key = getPostKey(post, index);
        const data = itemData.get(key);
        const measuredHeight = measuredHeights.get(key) || data?.style.height || FIXED_ITEM_HEIGHT;
        return { key, measuredHeight };
      })
      .sort((a, b) => {
        // Sort by height to help balance columns (optional optimization)
        return a.measuredHeight - b.measuredHeight;
      });

    // Distribute each item to the shortest column
    itemsWithHeights.forEach(({ key, measuredHeight }) => {
      // Find shortest column
      let shortestColumn = 0;
      for (let i = 1; i < columnCount; i++) {
        if (columnHeights[i] < columnHeights[shortestColumn]) {
          shortestColumn = i;
        }
      }

      // Add item to shortest column
      columns[shortestColumn].push(key);
      columnHeights[shortestColumn] += measuredHeight + gap;
    });

    // Only update if layout actually changed to prevent infinite loops
    setColumnsLayout((prevLayout) => {
      // Deep comparison to check if layout actually changed
      if (prevLayout.length !== columns.length) {
        return columns;
      }

      for (let i = 0; i < prevLayout.length; i++) {
        if (prevLayout[i].length !== columns[i].length) {
          return columns;
        }
        for (let j = 0; j < prevLayout[i].length; j++) {
          if (prevLayout[i][j] !== columns[i][j]) {
            return columns;
          }
        }
      }

      // Layout hasn't changed, return previous to prevent re-render
      return prevLayout;
    });
  }, [displayedPosts, columnCount, gap, itemData, measuredHeights]);

  // Recalculate layout when heights are measured or column count changes
  // Only recalculate when necessary to avoid infinite loops
  useEffect(() => {
    if (displayedPosts.length === 0 || columnCount === 0) {
      setColumnsLayout([]);
      return;
    }

    // Only distribute if we have measured heights or if it's the initial render
    if (measuredHeights.size > 0 || columnsLayout.length === 0) {
      distributeToColumns();
    }
  }, [
    displayedPosts.length,
    columnCount,
    gap,
    measuredHeights.size,
    distributeToColumns,
    // REMOVED: columnsLayout.length - this was causing the infinite loop
  ]);

  // ResizeObserver to measure actual item heights
  useEffect(() => {
    if (columnCount === 0 || displayedPosts.length === 0) return;

    const observer = new ResizeObserver((entries) => {
      setMeasuredHeights((prevHeights) => {
        const newHeights = new Map(prevHeights);
        let hasChanges = false;

        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          const postKey = element.dataset.postKey;
          if (postKey) {
            const key = !isNaN(Number(postKey)) ? Number(postKey) : postKey;
            const height = entry.contentRect.height;
            const prevHeight = prevHeights.get(key);

            if (prevHeight !== height) {
              newHeights.set(key, height);
              hasChanges = true;
            }
          }
        });

        // Only return new map if heights actually changed
        return hasChanges ? newHeights : prevHeights;
      });
    });

    resizeObserverRef.current = observer;

    // Observe all items in the main container
    itemRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [displayedPosts.length, columnCount]);

  // Handle facet toggle
  const toggleFacet = useCallback((tag: string) => {
    setSelectedFacets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  }, []);

  // Clear all facets
  const clearFacets = useCallback(() => {
    setSelectedFacets(new Set());
  }, []);

  // Use refs to always have latest values in callbacks
  const isLoadingRef = useRef(isLoading);
  const hasMoreDataRef = useRef(hasMoreData);
  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    hasMoreDataRef.current = hasMoreData;
    currentPageRef.current = currentPage;
  }, [isLoading, hasMoreData, currentPage]);

  // Load more posts from API when scrolling to bottom
  // Uses the /sbd/flatten/inquiry endpoint for paginated blog posts
  const loadMorePosts = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreDataRef.current) {
      return;
    }
    setIsLoading(true);

    try {
      const domain = typeof window !== "undefined" ? resolveCmsDomain(window.location.host) : "";
      const nextPage = currentPageRef.current + 1;

      const { value: responseData, error } = await clientFetcher.getBlogPosts({
        domain,
        page: nextPage,
        size: ITEMS_PER_BATCH,
      });

      if (error) {
        throw new Error(`Failed to fetch more posts: ${error.message || error}`);
      }

      // Extract posts from the response structure
      const newPosts: BlogPost[] = responseData?.data || [];
      const paging = responseData?.paging;

      if (newPosts.length > 0) {
        setAllLoadedPosts((prev) => [...prev, ...newPosts]);
        setCurrentPage(nextPage);
      }

      // Determine if there's more data based on paging info
      if (paging && typeof paging.total === "number") {
        const totalLoaded = allLoadedPosts.length + newPosts.length;
        setHasMoreData(totalLoaded < paging.total);
      } else {
        // If we got fewer items than requested, no more data
        setHasMoreData(newPosts.length >= ITEMS_PER_BATCH);
      }
    } catch (error) {
      console.error("[BlogWrapper] Error loading more posts:", error);
    } finally {
      setIsLoading(false);
    }
  }, [allLoadedPosts.length]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry.isIntersecting && hasMoreDataRef.current && !isLoadingRef.current) {
          loadMorePosts();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      },
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
      observer.disconnect();
    };
  }, [loadMorePosts]);

  const columnWidth = useMemo(() => {
    if (columnCount === 0) return "100%";
    return `calc((100% - ${(columnCount - 1) * gap}px) / ${columnCount})`;
  }, [columnCount, gap]);

  // Get share text helper
  const getShareText = useCallback((post: BlogPost) => {
    if (typeof window === "undefined") return "";
    const baseUrl = window.location.origin;
    const slug = post.slug?.startsWith("/") ? post.slug.slice(1) : post.slug;
    const url = `${baseUrl}/${slug}`;
    const title = post.name || "Check out this article";
    const description = post.description || "";
    return description ? `${title}\n\n${description}\n\n${url}` : `${title}\n\n${url}`;
  }, []);

  // Share handler function
  const handleShare = useCallback(
    (e: React.MouseEvent, post: BlogPost, platform: string) => {
      e.preventDefault();
      e.stopPropagation();

      if (typeof window === "undefined") return;

      const baseUrl = window.location.origin;
      const slug = post.slug?.startsWith("/") ? post.slug.slice(1) : post.slug;
      const url = `${baseUrl}/${slug}`;
      const title = post.name || "Check out this article";
      const description = post.description || "";

      // Format share text with title, description, and URL
      const shareText = description ? `${title}\n\n${description}\n\n${url}` : `${title}\n\n${url}`;

      // Always copy to clipboard first
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText);
        const postKey = getPostKey(
          post,
          displayedPosts.findIndex((p) => p.id === post.id || p.slug === post.slug),
        );
        setCopiedPostId(postKey);
        setTimeout(() => setCopiedPostId(null), 2000);
      }

      switch (platform) {
        case "facebook":
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            "_blank",
            "width=600,height=400",
          );
          break;
        case "linkedin":
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
            "_blank",
            "width=600,height=400",
          );
          break;
        case "twitter":
          window.open(
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
            "_blank",
            "width=600,height=400",
          );
          break;
        case "native":
          if (navigator.share) {
            navigator
              .share({
                title,
                text: description || title,
                url,
              })
              .catch((err) => {
                console.log("Share cancelled or failed:", err);
              });
          }
          break;
      }
    },
    [displayedPosts, getShareText],
  );

  // Render item component - memoized to prevent unnecessary re-renders
  const renderItem = useCallback(
    (key: string | number) => {
      const data = itemData.get(key);
      if (!data) return null;

      const { post, style } = data;
      // Use the stored height and backgroundColor from itemData
      const measuredHeight = measuredHeights.get(key) || style.height || FIXED_ITEM_HEIGHT;
      const backgroundColor = style.backgroundColor;

      return (
        <div
          key={key}
          ref={(el) => {
            if (el) {
              itemRefs.current.set(key, el);
              el.dataset.postKey = String(key);
            } else {
              itemRefs.current.delete(key);
            }
          }}
          style={{
            marginBottom: `${gap}px`,
            position: "relative",
            width: "100%",
          }}
          data-post-key={String(key)}
        >
          <article
            className="rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group overflow-hidden h-full flex flex-col"
            style={{
              height: `${measuredHeight}px`,
              backgroundColor: backgroundColor,
              transform: "scale(1)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.02)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Link
              href={`/${post.slug}`}
              className="relative z-10 block flex-1 touch-manipulation flex flex-col"
            >
              <div className="relative w-full h-32 flex-shrink-0 overflow-hidden rounded-t-xl">
                {post.thumbnail ? (
                  <Image
                    src={post.thumbnail}
                    alt={post.name}
                    fill
                    className="pointer-events-none object-cover transition-transform duration-300 group-hover:scale-110"
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">No image</span>
                  </div>
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="px-4 pt-4 pb-0 flex flex-col">
                <h2 className="text-sm font-semibold mb-1.5 line-clamp-2 text-gray-800 group-hover:text-gray-900 transition-colors duration-300">
                  {post.name}
                </h2>
                <p className="text-gray-700 text-xs leading-relaxed line-clamp-2 group-hover:text-gray-800 transition-colors duration-300 mb-0">
                  {post.description}
                </p>
              </div>
            </Link>
            {/* Social Sharing Buttons - Outside Link to prevent navigation */}
            <div
              className="px-4 pb-2 pt-0 border-t border-gray-200/30 flex items-center justify-center gap-1 flex-shrink-0"
              style={{ backgroundColor: backgroundColor, marginTop: "-1px" }}
            >
              {(() => {
                const postKey = getPostKey(
                  post,
                  displayedPosts.findIndex((p) => p.id === post.id || p.slug === post.slug),
                );
                const isCopied = copiedPostId === postKey;
                return (
                  <>
                    <button
                      onClick={(e) => handleShare(e, post, "facebook")}
                      className="p-1 rounded-full hover:bg-white/50 active:scale-90 transition-all duration-150 relative z-10 opacity-70 hover:opacity-100"
                      title="Share on Facebook"
                      aria-label="Share on Facebook"
                      type="button"
                    >
                      <FaFacebook size={12} className="text-[#1877F2]" />
                    </button>
                    <button
                      onClick={(e) => handleShare(e, post, "linkedin")}
                      className="p-1 rounded-full hover:bg-white/50 active:scale-90 transition-all duration-150 relative z-10 opacity-70 hover:opacity-100"
                      title="Share on LinkedIn"
                      aria-label="Share on LinkedIn"
                      type="button"
                    >
                      <FaLinkedin size={12} className="text-[#0077B5]" />
                    </button>
                    <button
                      onClick={(e) => handleShare(e, post, "twitter")}
                      className="p-1 rounded-full hover:bg-white/50 active:scale-90 transition-all duration-150 relative z-10 opacity-70 hover:opacity-100"
                      title="Share on X (Twitter)"
                      aria-label="Share on X (Twitter)"
                      type="button"
                    >
                      <FaXTwitter size={12} className="text-gray-700" />
                    </button>
                    {nativeShareAvailable ? (
                      <button
                        onClick={(e) => handleShare(e, post, "native")}
                        className="p-1 rounded-full hover:bg-white/50 active:scale-90 transition-all duration-150 relative z-10 opacity-70 hover:opacity-100"
                        title="Share via native share"
                        aria-label="Share via native share"
                        type="button"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-gray-600"
                        >
                          <circle cx="18" cy="5" r="3" />
                          <circle cx="6" cy="12" r="3" />
                          <circle cx="18" cy="19" r="3" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                      </button>
                    ) : null}
                    {isCopied && (
                      <span className="text-xs text-[#00A650] font-medium ml-1">Copied!</span>
                    )}
                  </>
                );
              })()}
            </div>
          </article>
        </div>
      );
    },
    [
      itemData,
      measuredHeights,
      gap,
      handleShare,
      displayedPosts,
      copiedPostId,
      nativeShareAvailable,
    ],
  );

  const isMobile = deviceType === "mobile";
  const isFold = deviceType === "fold";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";
  const showCollapsibleFilter = isMobile || isFold || isTablet;
  const showSidebarFilter = isDesktop;

  // Toggle filter panel
  const toggleFilter = useCallback(() => {
    setIsFilterOpen((prev) => !prev);
  }, []);

  // Render filter content (shared between sidebar and collapsible)
  const renderFilterContent = (isCollapsible = false) => (
    <div
      className={`space-y-2 ${isCollapsible ? "max-h-[40vh]" : "max-h-[calc(100vh-200px)]"} overflow-y-auto`}
    >
      {facetData.length > 0 ? (
        facetData.map(({ tag, count }) => {
          const isSelected = selectedFacets.has(tag);
          return (
            <button
              key={tag}
              onClick={() => toggleFacet(tag)}
              className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 ${
                isCollapsible ? "text-sm" : "px-4 py-3"
              } ${
                isSelected
                  ? "bg-[#00A650] text-white shadow-md"
                  : "bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium truncate mr-2">{tag}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                    isSelected ? "bg-white/20 text-white" : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {count}
                </span>
              </div>
            </button>
          );
        })
      ) : (
        <p className="text-gray-500 text-sm">No tags available</p>
      )}
    </div>
  );

  // Render selected tags as pills (for mobile/tablet compact view)
  const renderSelectedTagsPills = () => {
    if (selectedFacets.size === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {Array.from(selectedFacets).map((tag) => (
          <button
            key={tag}
            onClick={() => toggleFacet(tag)}
            className="inline-flex items-center gap-1 px-3 py-1 bg-[#00A650] text-white text-xs rounded-full hover:bg-[#008a42] transition-colors"
          >
            <span>{tag}</span>
            <FiX size={12} />
          </button>
        ))}
      </div>
    );
  };

  return (
    <section className={`w-full bg-[#E9F8EF] py-8 md:py-12 ${className}`}>
      <div className="w-[95%] sm:w-[90%] lg:w-[85%] xl:w-[80%] mx-auto px-3 sm:px-4 md:px-6 lg:px-10">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">{sectionTitle}</h1>
        <h2 className="mb-6 text-sm text-gray-600">
          Tổng hợp bài viết, chia sẻ kinh nghiệm và xu hướng mới.
        </h2>
        {/* Collapsible Filter for Mobile/Fold/Tablet */}
        {showCollapsibleFilter && (
          <div className="mb-4">
            <button
              onClick={toggleFilter}
              className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center gap-2">
                <FiFilter className="text-[#00A650]" size={18} />
                <span className="font-semibold text-gray-800">Filter by Tags</span>
                {selectedFacets.size > 0 && (
                  <span className="bg-[#00A650] text-white text-xs px-2 py-0.5 rounded-full">
                    {selectedFacets.size}
                  </span>
                )}
              </div>
              {isFilterOpen ? (
                <FiChevronUp className="text-gray-600" size={20} />
              ) : (
                <FiChevronDown className="text-gray-600" size={20} />
              )}
            </button>

            {/* Collapsible Filter Panel */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isFilterOpen
                  ? "max-h-[50vh] opacity-100 mt-2 pointer-events-auto"
                  : "max-h-0 opacity-0 pointer-events-none"
              }`}
            >
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-800">Tags</h3>
                  {selectedFacets.size > 0 && (
                    <button
                      onClick={clearFacets}
                      className="text-sm text-[#00A650] hover:text-[#008a42] transition-colors underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                {/* Grid layout for fold/tablet devices */}
                {isFold || isTablet ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto">
                    {facetData.length > 0 ? (
                      facetData.map(({ tag, count }) => {
                        const isSelected = selectedFacets.has(tag);
                        return (
                          <button
                            key={tag}
                            onClick={() => toggleFacet(tag)}
                            className={`text-left px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                              isSelected
                                ? "bg-[#00A650] text-white shadow-md"
                                : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-medium truncate">{tag}</span>
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                  isSelected
                                    ? "bg-white/20 text-white"
                                    : "bg-gray-200 text-gray-600"
                                }`}
                              >
                                {count}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-sm col-span-full">No tags available</p>
                    )}
                  </div>
                ) : (
                  renderFilterContent(true)
                )}
              </div>
            </div>

            {/* Show selected tags as pills when filter is collapsed */}
            {!isFilterOpen && renderSelectedTagsPills()}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Sidebar Filter for Desktop */}
          {showSidebarFilter && (
            <div className="w-full lg:w-[20%] xl:w-[18%] flex-shrink-0">
              <div className="bg-white rounded-lg shadow-md p-4 xl:p-6 sticky top-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base xl:text-lg font-semibold text-gray-800">Tags</h3>
                  {selectedFacets.size > 0 && (
                    <button
                      onClick={clearFacets}
                      className="text-sm text-[#00A650] hover:text-[#008a42] transition-colors underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                {renderFilterContent(false)}
              </div>
            </div>
          )}

          {/* Masonry Grid - responsive width based on device */}
          <div ref={containerRef} className="relative flex-1 w-full">
            {/* Column containers */}
            {columnsLayout.length > 0 ? (
              <div
                className="flex"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: `${gap}px`,
                  width: "100%",
                  alignItems: "flex-start",
                }}
              >
                {columnsLayout.map((columnKeys, colIndex) => (
                  <div
                    key={colIndex}
                    className="flex flex-col"
                    style={{
                      width: columnWidth,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {columnKeys.map((key) => renderItem(key))}
                  </div>
                ))}
              </div>
            ) : (
              /* Initial render: distribute items evenly while measuring */
              <div
                className="flex"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: `${gap}px`,
                  width: "100%",
                  alignItems: "flex-start",
                }}
              >
                {Array.from({ length: columnCount }).map((_, colIndex) => (
                  <div
                    key={colIndex}
                    style={{
                      width: columnWidth,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {displayedPosts
                      .map((post, index) => ({ post, index }))
                      .filter(({ index }) => index % columnCount === colIndex)
                      .map(({ post, index }) => {
                        // Use the actual index from displayedPosts array
                        const key = getPostKey(post, index);
                        return renderItem(key);
                      })}
                  </div>
                ))}
              </div>
            )}

            {/* Observer target for infinite scroll */}
            {hasMoreData && (
              <div
                ref={observerTarget}
                className="w-full h-20 flex items-center justify-center mt-4"
              >
                {isLoading && (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-[#00A650] rounded-full animate-bounce" />
                    <div
                      className="w-4 h-4 bg-[#00A650] rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    />
                    <div
                      className="w-4 h-4 bg-[#00A650] rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogWrapper;
